import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import toast from "react-hot-toast";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { auth, storage } from "../../helper/firebaseClient";
import useAuth from "../../stores/authStore";
import fileContext from "../../context/fileContext";
import { io } from "socket.io-client";
import {
  DocumentEditor,
  HyperlinkDialog,
  Inject,
} from "@syncfusion/ej2-react-documenteditor";
import handleTextExtraction from "../../helper/fileExtraction";

import {
  CollaborativeEditingHandler,
  DocumentEditorContainerComponent,
  Toolbar,
  WordExport,
} from "@syncfusion/ej2-react-documenteditor";

import "../../styles/DocEditor_styles.css";
import "../../styles/material.css";

import { generateFileVersionId } from "../../utils/helpers";
import { api } from "../../utils/axios-instance";

const CACHE_NAME = "blob-cache";

// Syncfusion service URL
// The public Syncfusion demo server
//   https://ej2services.syncfusion.com/production/web-services/api/documenteditor/
// has been decommissioned and returns "resource removed" errors.
//
// Set REACT_APP_SYNCFUSION_SERVICE_URL in your .env to your own hosted service.
// See: https://ej2.syncfusion.com/documentation/document-editor/web-services/
// For local dev you can run the Node.js service on port 5000.

// Syncfusion service URL fix
// The editor appends action names directly to serviceUrl, e.g.:
//   serviceUrl + "SpellCheckByPage"
//   serviceUrl + "Import"
//
// ERROR:  "Cannot POST /SpellCheckByPage"
//   Cause:  .env = "http://13.203.231.109:5000"
//             → called http://13.203.231.109:5000/SpellCheckByPage  ✗
//   Fix:    .env = "http://13.203.231.109:5000/api/documenteditor/"
//             → calls  http://13.203.231.109:5000/api/documenteditor/SpellCheckByPage  ✓
//
// Update your .env file:
//   REACT_APP_SYNCFUSION_SERVICE_URL=http://13.203.231.109:5000/api/documenteditor/
//
// The code below also auto-corrects a host-only URL so the app keeps working
// even if someone sets the .env without the path.
const _rawServiceUrl = process.env.REACT_APP_SYNCFUSION_SERVICE_URL || "";
const SERVICE_URL = (() => {
  if (!_rawServiceUrl) return "";
  // Already has the documenteditor path
  if (_rawServiceUrl.includes("documenteditor")) {
    return _rawServiceUrl.endsWith("/") ? _rawServiceUrl : _rawServiceUrl + "/";
  }
  // Host only (e.g. http://13.203.231.109:5000) — append the required path
  const base = _rawServiceUrl.endsWith("/")
    ? _rawServiceUrl
    : _rawServiceUrl + "/";
  return base + "api/documenteditor/";
})();

const debounce = (func, delay) => {
  let timer;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => func.apply(this, args), delay);
  };
};

export default function TextEditor({
  preUrl,
  fileName,
  fileId,
  mimetype,
  setSaving,
  saving,
  securityDetails,
  currVersion,
  embedded = false,
  showComments = false,
}) {
  const isReadOnly = Boolean(embedded || securityDetails?.allowEdit === false);
  const editorRef = useRef(null);
  const [isEditorReady, setIsEditorReady] = useState(false);
  const [socket, setSocket] = useState(null);
  const [collaborativeEditingHandler, setCollaborativeEditingHandler] =
    useState();
  const profileData = useAuth((state) => state.profileData);
  const context = useContext(fileContext);

  const userAccess =
    securityDetails?.selective_access?.[profileData?.id]?.access ?? null;

  useEffect(() => {
    const s = io(process.env.REACT_APP_REALTIME_SERVER_URL);

    setSocket(s);

    s.emit("join-room", fileId, profileData?.email);

    return () => {
      s.disconnect();
    };
  }, [fileId, profileData?.email]);


  // For late joiners to a file being edited
  useEffect(() => {
    if (!socket || !editorRef.current) return;

    socket.on("load-document", (sfdt) => {
      console.log("Loading persisted document from Redis");
      editorRef.current.documentEditor.open(sfdt);
    });

    return () => socket.off("load-document");
  }, [socket]);

  useEffect(() => {
    console.warn("here");
    if (editorRef.current && preUrl.trim() !== "") {
      const fetchAndExtractContent = async (wordFileUrl) => {
        try {
          // Fetch the Word file
          const response = await fetch(wordFileUrl);
          if (!response.ok) {
            throw new Error("Failed to fetch the Word file");
          }

          // Convert response to blob
          const blob = await response.blob();
          const file = new File([blob], fileName, {
            type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          });

          context.setCurrFile(file);
          editorRef.current.documentEditor.open(file);

          // Only set read-only when user has an explicit "no edit" restriction.
          // userAccess === null means no restrictions — full edit allowed.
          if (userAccess && !userAccess?.includes("edit")) {
            editorRef.current.documentEditor.isReadOnly = true;
          }

          if (isReadOnly) {
            editorRef.current.documentEditor.isReadOnly = true;
          }
        } catch (error) {
          console.error("Error fetching and extracting content:", error);
        }
      };
      fetchAndExtractContent(preUrl);

      function handleKeyBoardShortcuts() {
        editorRef.current.documentEditor.keyDown = function (args) {
          let keyCode = args.event.which || args.event.keyCode;
          let isCtrlKey =
            args.event.ctrlKey || args.event.metaKey
              ? true
              : keyCode === 17
                ? true
                : false;

          if (isCtrlKey && keyCode === 83) {
            //To prevent default save operation, set the isHandled property to true
            args.isHandled = true;

            editorRef.current.documentEditor.save(
              fileName.split(".").slice(0, -1).join("."),
              "Docx"
            );

            args.event.preventDefault();
          }
        };
      }

      handleKeyBoardShortcuts();

      DocumentEditor.Inject(CollaborativeEditingHandler);
      editorRef.current.documentEditor.enableCollaborativeEditing = true;
      console.log(
        "Collaborative editing enabled:",
        editorRef.current.documentEditor.collaborativeEditingHandlerModule
      );
    }
  }, [preUrl, isEditorReady, fileName, userAccess, isReadOnly]);

  useEffect(() => {
    if (socket && editorRef.current && collaborativeEditingHandler) {
      socket.on("receive-changes", (delta) => {
        console.log("Received changes:", delta);
        collaborativeEditingHandler.applyRemoteAction("action", {
          operations: delta,
          connectionId: fileId,
          userId: profileData?.email,
          // version: 1,
        });

        // Update cache optimistically
        // auth.currentUser.getIdToken().then((token) => {
        //   const fileIdentifier = `${fileId}-${token}`;
        //   caches.open(CACHE_NAME).then((cache) => {
        //     cache.match(fileIdentifier).then((response) => {
        //       const fileVersionId = generateFileVersionId(fileId);
        //       if (response) {
        //         cache.put(
        //           fileIdentifier,
        //           new Response(response.body, {
        //             headers: {
        //               "Content-Type": mimetype,
        //               "sw-cache-date": new Date().toISOString(),
        //               "x-file-version-id": fileVersionId,
        //             },
        //           })
        //         );
        //       }
        //     });
        //   });
        // });

        // Update cache
        // const token = auth.currentUser.getIdToken();
        // const fileIdentifier = `${fileId}-${token}`;
        // const cache = caches.open(CACHE_NAME);

        // cache.then((cache) => {
        //   cache.match(fileIdentifier).then((response) => {
        //     const fileVersionId = generateFileVersionId(fileId);
        //     if (response) {
        //       const updatedResponse = new Response(response.body, {
        //         headers: {
        //           "Content-Type": mimetype,
        //           "sw-cache-date": new Date().toISOString(),
        //           "x-file-version-id": fileVersionId,
        //         },
        //       });
        //       cache.put(fileIdentifier, updatedResponse);
        //       console.log("Cache updated for file:", fileIdentifier);
        //     }
        //   });
        // });
      });
    }

    return () => {
      if (socket) {
        socket.off("receive-changes");
      }
    };
  }, [
    collaborativeEditingHandler,
    fileId,
    mimetype,
    profileData?.email,
    socket,
  ]);

  useEffect(() => {
    const handleTabClose = async (event) => {
      if (!editorRef.current) return;
      const doc = await editorRef.current.documentEditor.saveAsBlob("Docx");

      if (!doc) {
        console.error("Failed to generate document blob.");
        return;
      }
      // Make the API call before the alert is shown
      await handleTextExtraction(
        doc,
        fileId,
        fileName,
        "red",
        profileData,
        true
      );

      // Show the confirmation dialog
      event.preventDefault();
      event.returnValue = "Are you sure you want to exit?"; // This triggers the alert for the user
    };

    window.addEventListener("beforeunload", handleTabClose);
    return () => {
      window.removeEventListener("beforeunload", handleTabClose);
    };
  }, [fileId, fileName, profileData]);

  // Save document to Firebase and cache
  const saveDocument = useCallback(
    async (showPopup, args) => {
      if (!isEditorReady) return;
      setSaving(true);

      const doc = await editorRef.current.documentEditor.saveAsBlob("Docx");

      if (!doc) {
        console.error("Failed to generate document blob.");
        setSaving(false);
        return;
      }

      context.setCurrFile(doc);

      // currVersion is the storage object UUID for this file.
      // Fall back to fileId for files uploaded before versioning was added.
      const storagePath = fileId;
      console.log(
        "Saving to path:",
        `files/${profileData?.org_id}/${storagePath}`
      );

      const fileRef = ref(
        storage,
        `files/${profileData?.org_id}/${storagePath}`
      );
      const metadata = {
        customMetadata: {
          department_id: profileData?.dept,
          org_id: profileData?.org_id,
        },
      };

      const uploadTask = uploadBytesResumable(fileRef, doc, metadata);

      uploadTask.on(
        "state_changed",
        null,
        (error) => {
          console.error("File upload error:", error);
          setSaving(false);
        },
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          console.log("File available at:", downloadURL);

          const fileVersionId = generateFileVersionId(fileId);
          const token = await auth.currentUser?.getIdToken();
          const fileIdentifier = `${fileId}-${token}`;

          // send the exact key string the backend expects.
          try {
            await api.post(`file/update-file-version/${fileId}/`, {
              download_url: downloadURL,
              idToken: token,
            });
          } catch (err) {
            // Non-fatal — version metadata update failure does not stop editing.
            console.warn(
              "update-file-version failed (non-fatal):",
              err?.response?.status,
              err?.response?.data
            );
          }

          // await api.post(`file/update-file-version/${fileId}/`, {
          //   versionId: fileVersionId,
          //   idToken: token,
          // });

          const cache = await caches.open(CACHE_NAME);
          await cache.delete(fileIdentifier);
          setSaving(false);
          if (showPopup) toast.success("File saved and cached successfully.");
        }
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isEditorReady,
      fileId,
      currVersion,
      mimetype,
      profileData?.dept,
      profileData?.org_id,
      profileData?.email,
      socket,
    ]
  );

  // Create a debounced version of the saveDocument function
  const debouncedSaveDocument = useCallback(debounce(saveDocument, 300), [
    saveDocument,
  ]);

  const toolItem = {
    prefixIcon: "e-save icon",
    tooltipText: "Save the Document",
    text: "Save",
    id: "save",
  };

  let items = [
    toolItem,
    "New",
    "Open",
    "Separator",
    "Undo",
    "Redo",
    "Separator",
    "Image",
    "Table",
    "Hyperlink",
    "Bookmark",
    "TableOfContents",
    "Separator",
    "Header",
    "Footer",
    "PageSetup",
    "PageNumber",
    "Break",
    "InsertFootnote",
    "InsertEndnote",
    "Separator",
    "Find",
    "Separator",
    "Comments",
    "TrackChanges",
    "Separator",
    "LocalClipboard",
    "RestrictEditing",
    "Separator",
    "FormFields",
    "UpdateFields",
    "ContentControl",
  ];

  if (isReadOnly || (userAccess && !userAccess?.includes("edit"))) {
    items = [
      // toolItem,
      "Separator",
      "Undo",
      "Redo",
      "Separator",
      "Image",
      "Table",
      "Hyperlink",
      "Bookmark",
      "TableOfContents",
      "Separator",
      "Header",
      "Footer",
      "PageSetup",
      "PageNumber",
      "Break",
      "InsertFootnote",
      "InsertEndnote",
      "Separator",
      "Find",
      "Separator",
      "Comments",
      "TrackChanges",
      "Separator",
      "LocalClipboard",
      // "RestrictEditing",
      "Separator",
      "FormFields",
      "UpdateFields",
      "ContentControl",
    ];
  }

  const onToolbarClick = (args) => {
    switch (args.item.id) {
      case "save":
        saveDocument(true);
        break;
      default:
        break;
    }
  };

  function onCreated() {
    setIsEditorReady(true);

    setCollaborativeEditingHandler(
      editorRef.current.documentEditor.collaborativeEditingHandlerModule
    );

    console.log(collaborativeEditingHandler, editorRef.current.documentEditor);
  }

  useEffect(() => {
    setTimeout(() => {
      const linkInput = document.querySelector(".e-rte-link-dialog input");
      if (linkInput) {
        linkInput.focus();
      }
    }, 300);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (editorRef.current?.documentEditor) {
        editorRef.current.documentEditor.resize();
      }
      window.dispatchEvent(new Event("resize"));
    }, 200);

    return () => clearTimeout(timer);
  }, [showComments]);

  const calculatedHeight = embedded
    ? "calc(100vh - 120px)"
    : context.isMultiSelect
      ? "80vh"
      : `${window.innerHeight - 64}px`;

  return (
    <div
      className={showComments ? "comments-open" : "comments-closed"}
      style={{
        flex: 1,
        minHeight: 0,
        height: "100%",
        width: "100%",
      }}
    >
      <DocumentEditorContainerComponent
        id="container"
        height={calculatedHeight}
        width="100%"
        serviceUrl={`${process.env.REACT_APP_SYNCFUSION_URL}/word/api/documenteditor/`}
        enableToolbar={!isReadOnly}
        ref={(scope) => {
          editorRef.current = scope;
        }}
        toolbarItems={items}
        toolbarClick={onToolbarClick}
        created={onCreated}
        contentChange={(args) => {
          if (args) {
            const sfdt = editorRef.current.documentEditor.serialize();

            socket.emit("send-changes", {
              fileId,
              operations: args.operations,
              sfdt,
            });
          }
          debouncedSaveDocument(false, args);
        }}
        currentUser={profileData?.email}
        style={{ display: "block", padding: 0, width: "100%", flex: 1 }}
        enableAutoFocus={true}
        enableSpellCheck={!!SERVICE_URL}
        enableLocalPaste={true}
        enableHyperlinkDialog={true}
      >
        <Inject services={[Toolbar, WordExport, HyperlinkDialog]} />
      </DocumentEditorContainerComponent>
    </div>
  );
}
